import { useDeviceType } from '@/hooks/use-device-type';
import {
  getAssignPayloadKey,
  getSelectedAssignPayload,
  isSameAssignPayload,
  SaleAssignDragPayload,
  subscribeSelectedAssign,
  toggleSelectedAssign,
} from '@/pages/sale/lib/sale-comment-drag';
import { getAssignRowStateClassName } from '@/pages/sale/lib/sale-assign-row-utils';
import { useDraggable } from '@dnd-kit/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UseSaleAssignDraggableOptions = {
  disabled?: boolean;
  enableTapSelect?: boolean;
};

const TAP_MOVE_THRESHOLD_PX = 10;

export function useSaleAssignDraggable(
  payload: SaleAssignDragPayload,
  options: UseSaleAssignDraggableOptions = {},
) {
  const { disabled = false, enableTapSelect = false } = options;
  const { isMobile, isTablet } = useDeviceType();
  const isTouchDevice = isMobile || isTablet;

  const suppressNextTapRef = useRef(false);
  const wasDraggingRef = useRef(false);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const movedDuringPressRef = useRef(false);
  const [isSelected, setIsSelected] = useState(false);

  const payloadKey = getAssignPayloadKey(payload);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `assign-${payloadKey}`,
    data: payload,
    disabled,
  });

  useEffect(() => {
    const syncSelection = () => {
      const selected = getSelectedAssignPayload();
      setIsSelected(isSameAssignPayload(selected, payload));
    };
    syncSelection();
    return subscribeSelectedAssign(syncSelection);
  }, [payload]);

  useEffect(() => {
    if (isDragging) {
      wasDraggingRef.current = true;
      return;
    }
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      suppressNextTapRef.current = true;
    }
  }, [isDragging]);

  const wrapTouchListeners = useCallback((baseListeners: Record<string, Function>) => {
    const rowListeners: Record<string, unknown> = {};

    Object.entries(baseListeners).forEach(([eventName, handler]) => {
      if (typeof handler !== 'function') return;

      if (eventName === 'onPointerDown') {
        rowListeners.onPointerDown = (event: React.PointerEvent) => {
          pointerStartRef.current = { x: event.clientX, y: event.clientY };
          movedDuringPressRef.current = false;
          handler(event);
        };
        return;
      }

      if (eventName === 'onPointerMove') {
        rowListeners.onPointerMove = (event: React.PointerEvent) => {
          const start = pointerStartRef.current;
          if (start) {
            const movedX = Math.abs(event.clientX - start.x);
            const movedY = Math.abs(event.clientY - start.y);
            if (movedX > TAP_MOVE_THRESHOLD_PX || movedY > TAP_MOVE_THRESHOLD_PX) {
              movedDuringPressRef.current = true;
            }
          }
          handler(event);
        };
        return;
      }

      if (eventName === 'onPointerUp' || eventName === 'onPointerCancel') {
        rowListeners[eventName] = (event: React.PointerEvent) => {
          pointerStartRef.current = null;
          handler(event);
        };
        return;
      }

      rowListeners[eventName] = handler;
    });

    return rowListeners;
  }, []);

  const draggableProps = useMemo(() => {
    if (disabled) return {};

    if (!isTouchDevice) {
      return { ...attributes, ...(listeners ?? {}) };
    }

    return {
      ...attributes,
      ...wrapTouchListeners(listeners ?? {}),
    };
  }, [attributes, disabled, isTouchDevice, listeners, wrapTouchListeners]);

  const handleRowClick = useCallback(
    (event: React.MouseEvent, onFallback?: () => void) => {
      if (disabled || isDragging) return;

      if (movedDuringPressRef.current) {
        movedDuringPressRef.current = false;
        return;
      }

      if (suppressNextTapRef.current) {
        suppressNextTapRef.current = false;
        return;
      }

      if (enableTapSelect) {
        event.stopPropagation();
        toggleSelectedAssign(payload);
        return;
      }

      onFallback?.();
    },
    [disabled, enableTapSelect, isDragging, payload],
  );

  const stateClassName = getAssignRowStateClassName({
    isDragging,
    isSelected,
    tapAssignEnabled: enableTapSelect,
  });

  return {
    setNodeRef,
    isDragging,
    isSelected,
    draggableProps,
    handleRowClick,
    tapAssignEnabled: enableTapSelect,
    stateClassName,
  };
}
